import React, { useEffect, useLayoutEffect } from 'react'
import { Button, Select } from '@enamelai/design-system'
import {
  CHARTING_YES_NO_OPTIONS,
  DEFAULT_CHARTING_VIEW_OPTIONS,
  SHOW_GROUP_PROCEDURES_BY_TOOTH_SETTING,
  SHOW_RESTORE_DEFAULTS_BUTTON,
  TOOTH_NUMBERING_OPTIONS,
} from '@constants/Settings/chartingPreferences.constants'
import {
  ChartingYesNo,
  IChartingSystemPreferences,
  ITreatmentPlanPreferences,
} from '@dataTypes/chartingPreferences.type'
import { sanitizeDefaultChartingView } from '@utils/default-charting-view.utils'
import ChartingPreferenceTeethPreview from './ChartingPreferenceTeethPreview.component'
import useChartingPreferences from './useChartingPreferences'
import './ChartingPreferencesStyle.css'

type YesNoKey<T> = {
  [K in keyof T]: T[K] extends ChartingYesNo ? K : never
}[keyof T]

const CHARTING_SYSTEM_YES_NO_FIELDS: {
  key: YesNoKey<IChartingSystemPreferences>
  label: string
}[] = [
  { key: 'show_existing_conditions', label: 'Show existing conditions' },
  { key: 'show_missing_teeth', label: 'Show missing teeth' },
  { key: 'show_primary_teeth', label: 'Show primary teeth' },
  { key: 'show_completed_procedures', label: 'Show completed procedures' },
]

const TREATMENT_PLAN_FIELDS: {
  key: YesNoKey<ITreatmentPlanPreferences>
  label: string
}[] = [
  ...(SHOW_GROUP_PROCEDURES_BY_TOOTH_SETTING
    ? [
        {
          key: 'group_procedures_by_tooth' as const,
          label: 'Group procedures by tooth',
        },
      ]
    : []),
  {
    key: 'auto_mark_completed_when_charted',
    label: 'Auto-mark treatment as completed when charted',
  },
  { key: 'allow_manual_fee_override', label: 'Allow manual fee override' },
  { key: 'show_insurance_estimate', label: 'Show insurance estimate' },
]

const ChartingPreferences = (): React.ReactElement => {
  const {
    discardChanges,
    form,
    isDirty,
    loading,
    resetToDefault,
    saveChanges,
    setChartingSystemField,
    setTreatmentPlanField,
    setHeaderButtons,
    setHeaderTitle,
  } = useChartingPreferences()
  const { charting_system: cs, treatment_plan: tp } = form

  useLayoutEffect(() => {
    setHeaderTitle('Charting preferences')
    setHeaderButtons(
      <>
        {SHOW_RESTORE_DEFAULTS_BUTTON && (
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={resetToDefault}
            disabled={loading}
          >
            Reset to default
          </Button>
        )}
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          onClick={discardChanges}
          disabled={loading || !isDirty}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={() => {
            void saveChanges()
          }}
          disabled={loading || !isDirty}
        >
          Save changes
        </Button>
      </>,
    )
  }, [
    discardChanges,
    isDirty,
    loading,
    resetToDefault,
    saveChanges,
    setHeaderButtons,
    setHeaderTitle,
  ])

  useEffect(() => {
    return () => {
      setHeaderTitle(null)
      setHeaderButtons(null)
    }
  }, [setHeaderButtons, setHeaderTitle])

  const yesNoSelect = (
    label: string,
    value: ChartingYesNo,
    onChange: (value: ChartingYesNo) => void,
  ): React.ReactNode => (
    <Select
      key={label}
      label={label}
      placeholder="Select"
      options={CHARTING_YES_NO_OPTIONS}
      value={value}
      disabled={loading}
      onChange={(next) => onChange(String(next) as ChartingYesNo)}
    />
  )

  return (
    <div className="settings-charting-preferences">
      <ChartingPreferenceTeethPreview
        showToothNumber={cs.show_tooth_number === 'yes'}
        toothNumberingSystem={cs.tooth_numbering_system}
      />
      <section className="settings-charting-preferences-section">
        <h2 className="settings-charting-preferences-section-title">
          Charting system
        </h2>
        <div className="settings-charting-preferences-fields">
          {yesNoSelect('Show tooth number', cs.show_tooth_number, (value) =>
            setChartingSystemField('show_tooth_number', value),
          )}
          <Select
            label="Tooth numbering system"
            placeholder="Select"
            options={TOOTH_NUMBERING_OPTIONS}
            value={cs.tooth_numbering_system}
            disabled={loading}
            onChange={(value) =>
              setChartingSystemField(
                'tooth_numbering_system',
                String(value) as typeof cs.tooth_numbering_system,
              )
            }
          />
          {CHARTING_SYSTEM_YES_NO_FIELDS.map(({ key, label }) =>
            yesNoSelect(label, cs[key], (value) =>
              setChartingSystemField(key, value),
            ),
          )}
          <Select
            label="Default charting view"
            placeholder="Select"
            multiple
            options={DEFAULT_CHARTING_VIEW_OPTIONS}
            value={cs.default_charting_view}
            disabled={loading}
            onChange={(value) =>
              setChartingSystemField(
                'default_charting_view',
                sanitizeDefaultChartingView(
                  (Array.isArray(value)
                    ? value
                    : [value]) as typeof cs.default_charting_view,
                ),
              )
            }
          />
          {yesNoSelect('Autosave changes', cs.autosave_changes, (value) =>
            setChartingSystemField('autosave_changes', value),
          )}
        </div>
      </section>
      <section className="settings-charting-preferences-section">
        <h2 className="settings-charting-preferences-section-title">
          Treatment plan
        </h2>
        <div className="settings-charting-preferences-fields">
          {TREATMENT_PLAN_FIELDS.map(({ key, label }) =>
            yesNoSelect(label, tp[key], (value) =>
              setTreatmentPlanField(key, value),
            ),
          )}
        </div>
      </section>
    </div>
  )
}

export default ChartingPreferences
